import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../../components/common/header";
import LoadingSpinnerFull from "../../../components/common/loading-spinner/loading-spinner-full";
import { OrderStatus } from "../../../components/order/order-status";
import { calculateCarbonEmission, formatDate } from "../page";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { useState, useRef } from "react";
import { GET_ORDER_DETAIL } from "../detail/page";
import { HiChevronDown, HiChevronUp, HiX } from "react-icons/hi";
import { track } from "@amplitude/analytics-browser";
import { getOrderType } from "../../../utils/order";
import { notifyError } from "../../../utils/toast";

const CREATE_REVIEW = gql`
  mutation CreateReview($input: CreateReviewInput!) {
    createReview(input: $input) {
      ok
      error
      review {
        id
        content
        reviewImages {
          img
        }
        createdAt
      }
    }
  }
`;

const OrderReview = () => {
  const { id } = useParams();
  const { data: orderData, loading } = useQuery(GET_ORDER_DETAIL, {
    variables: { id: parseInt(id as string) },
    fetchPolicy: "network-only",
  });
  const [createReview] = useMutation(CREATE_REVIEW);
  const [content, setContent] = useState("");
  const [imgs, setImgs] = useState<File[]>([]);
  const [imgPreviewUrls, setImgPreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejectReasonsOpen, setIsRejectReasonsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const MAX_IMAGES = 5;

  if (loading) {
    return <LoadingSpinnerFull />;
  }

  const handleUpload = async () => {
    if (content.length < 10) {
      notifyError("최소 10자 이상 작성해주세요!");
      return;
    }

    // 이미지 개수 최종 검증
    if (imgs.length > MAX_IMAGES) {
      notifyError(`이미지는 최대 ${MAX_IMAGES}장까지 업로드할 수 있어요.`);
      return;
    }

    let confirm;
    if (imgs.length === 0) {
      confirm = window.confirm(
        "포토리뷰로 작성하시면 스탬프 1개가 적립됩니다.\n(구매 확정 후 2일 이내 작성 시)\n\n일반 리뷰로 작성을 완료할까요?",
      );
    } else {
      confirm = window.confirm("리뷰를 작성 완료할까요?");
    }

    if (!confirm) {
      return;
    }

    try {
      setIsSubmitting(true);
      await createReview({
        variables: {
          input: {
            orderId: parseInt(id as string),
            content,
            imgs: imgs.length > 0 ? imgs : null,
          },
        },
        update: (cache, { data: { createReview } }) => {
          if (createReview.ok) {
            cache.modify({
              id: `Order:${parseInt(id as string)}`,
              fields: {
                review: () => createReview.review,
              },
            });
          }
        },
        onCompleted: (data) => {
          if (data?.createReview.ok) {
            track("리뷰 작성하기 완료");
            navigate("/order/review/complete");
          } else {
            notifyError(
              data?.createReview.error || "리뷰 작성 중 오류가 발생했습니다.",
            );
          }
        },
      });
    } catch (error) {
      notifyError("리뷰 작성 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 최대 개수 제한
    const remainingSlots = MAX_IMAGES - imgs.length;
    if (remainingSlots <= 0) {
      notifyError(`최대 ${MAX_IMAGES}장까지 업로드할 수 있어요.`);
      return;
    }

    // 선택한 파일이 남은 슬롯보다 많으면 알림 표시
    if (files.length > remainingSlots) {
      notifyError(
        `5장을 초과하여 선택하셨습니다. 최대 ${MAX_IMAGES}장만 가능해요.`,
      );
    }

    const filesToAdd = files.slice(0, remainingSlots);
    const newFiles = [...imgs, ...filesToAdd];
    const newPreviewUrls = [
      ...imgPreviewUrls,
      ...filesToAdd.map((file) => URL.createObjectURL(file)),
    ];

    setImgs(newFiles);
    setImgPreviewUrls(newPreviewUrls);

    // input 초기화 (같은 파일 다시 선택 가능하도록)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const newFiles = imgs.filter((_, i) => i !== index);
    const newPreviewUrls = imgPreviewUrls.filter((_, i) => i !== index);

    // 기존 preview URL 정리
    URL.revokeObjectURL(imgPreviewUrls[index]);

    setImgs(newFiles);
    setImgPreviewUrls(newPreviewUrls);
  };

  // 주문내역 이미지
  const orderImage =
    (orderData?.order?.products?.[0]?.img as string) ||
    (orderData?.order?.products?.[0]?.menus?.[0].img as string) ||
    (orderData?.order?.pack?.store?.img as string) ||
    "";

  // 주문 매장 이름
  const storeTitle =
    orderData?.order?.products?.[0]?.store.title ||
    orderData?.order?.pack?.store?.title;

  return (
    <div className="w-full min-h-screen h-full flex flex-col overflow-y-scroll">
      <Header title="리뷰 작성하기" showBackButton />

      <div className="w-full p-4 bg-white flex flex-col gap-4">
        <div>
          <div className="text-md">
            <span className="text-gray-700">
              {formatDate(orderData?.order?.createdAt)}{" "}
            </span>
            <span className="text-gray-700">· </span>
            <OrderStatus order={orderData?.order} />
          </div>
          <div className="w-full flex justify-start gap-1 mt-2">
            <div className="h-full flex justify-end items-end mr-2">
              <ResponsiveCDNImage
                className="w-full aspect-square rounded-md object-cover border border-gray-200 max-w-[100px]"
                src={orderImage}
                alt="팩 사진"
                sizeRatio={1 / 3}
              />
            </div>

            <div className="flex flex-col justify-between gap-1">
              <div>
                <div>
                  <span className="text-black text-lg font-semibold">
                    {storeTitle}
                  </span>
                </div>
                <div className="flex gap-1">
                  <span className="text-gray-700 text-md font-medium">
                    🎁 '
                    <span className="font-semibold">
                      {getOrderType(orderData?.order)}
                    </span>
                    ' 에 성공하셨어요!
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex">
                  <div className="px-1 py-2 bg-[#f3f3f3] rounded-md flex items-center">
                    <div className="text-black text-[10px] break-keep">
                      <span className="font-semibold">
                        {orderData?.order?.amount?.toLocaleString()}원
                      </span>
                      에 구매했어요!
                    </div>
                  </div>
                </div>
                <div className="flex">
                  <div className="px-1 py-2 bg-[#edfbdf] rounded-md flex items-center break-keep">
                    <div className="text-gray-700 text-[10px] font-medium break-keep">
                      <span>CO</span>
                      <span className="uppercase">
                        <sub>2</sub>
                      </span>
                      를
                      <span className="ml-1">
                        <span className="font-semibold">
                          {calculateCarbonEmission(orderData?.order?.amount)}kg
                        </span>
                        줄였어요!
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="w-full border-gray-200" />

        <div className="flex flex-col gap-2">
          <label className="text-gray-700 font-medium">
            사진 첨부 {imgs.length > 0 && `(${imgs.length}/${MAX_IMAGES})`}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {imgPreviewUrls.map((previewUrl, index) => (
              <div
                key={index}
                className="aspect-square border-2 border-gray-300 rounded-lg overflow-hidden relative group"
              >
                <img
                  src={previewUrl}
                  alt={`리뷰 이미지 ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => handleRemoveImage(e, index)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition-opacity"
                >
                  <HiX className="w-4 h-4" />
                </button>
              </div>
            ))}
            {imgs.length < MAX_IMAGES && (
              <div
                onClick={handleImageClick}
                className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
              >
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xl text-gray-400">+</span>
                  <span className="text-gray-500 text-xs text-center px-1">
                    {imgs.length === 0 ? "사진 선택" : "추가"}
                  </span>
                </div>
              </div>
            )}
            <input
              id="imageUrl"
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleFileChange}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div>
            <div className="text-gray-700 font-medium">
              마히팩 픽업 경험은 어떠셨나요?*
            </div>
            <div className="text-gray-500 text-md mt-0.5">
              소중한 경험을 사장님께 전해주세요 💛
            </div>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full border border-gray-200 rounded-md p-2 resize-none"
            placeholder="픽업하신 팩의 맛과 매장의 친절함에 대해 정성껏 칭찬해 주시면, 사장님의 하루가 즐거워질 수 있어요!"
            rows={5}
            maxLength={1000}
          />
          <div className="flex justify-end">
            <span className="text-gray-500 text-sm">
              {content.length}/1000자
            </span>
          </div>
        </div>

        {/* 리뷰 반려사유 알아보기 */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/80 overflow-hidden">
          <button
            type="button"
            onClick={() => setIsRejectReasonsOpen(!isRejectReasonsOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100/80 transition-colors"
          >
            <span className="text-gray-700 font-medium text-sm">
              리뷰 반려사유 알아보기
            </span>
            <span className="text-gray-500">
              {isRejectReasonsOpen ? (
                <HiChevronUp className="w-5 h-5" />
              ) : (
                <HiChevronDown className="w-5 h-5" />
              )}
            </span>
          </button>
          {isRejectReasonsOpen && (
            <div className="px-4 pb-4 pt-0 border-t border-gray-200">
              <div className="flex flex-col gap-3 text-gray-600 text-sm leading-relaxed pt-3">
                <p>
                  검수 과정에서 아래와 같은 리뷰는 반려될 수 있어요.
                </p>
                <ul className="space-y-2 list-disc pl-4">
                  <li>동일·중복 이미지나 멘트</li>
                  <li>매장과 무관한 사진(타매장 이미지, 직접 촬영하지 않은 이미지 등)</li>
                  <li>특정 메뉴 제공을 요구하는 내용</li>
                  <li>실제 이용 경험이 아닌 내용</li>
                  <li>허위 리뷰, 욕설·비방 등 운영 정책 위반</li>
                </ul>
                <p className="text-gray-500 text-xs">
                  반려 시 카카오채널로 문의하시면 자세한 사유를 안내해 드려요.
                </p>
              </div>
            </div>
          )}
        </div>

        <div>
          <button
            onClick={handleUpload}
            className="w-full p-3 border rounded-xl flex items-center justify-center bg-primary"
          >
            <span className="text-white font-semibold">작성완료</span>
          </button>
        </div>

        <hr className="w-full border-gray-200" />

        <div className="w-full text-gray-500 text-md break-keep text-center">
          피드백이나 궁금하신 점은
          <br />
          <a
            href="http://pf.kakao.com/_xawxcRn/chat"
            className="underline underline-offset-2 text-blue-500 font-semibold rounded-lg active:text-blue-600 transition duration-300"
          >
            마감히어로 카카오채널
          </a>
          을 이용해주세요!
        </div>
      </div>

      {isSubmitting && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <LoadingSpinnerFull />
        </div>
      )}
    </div>
  );
};

export default OrderReview;
